import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Search, Calendar as CalendarIcon, Users, Sparkles, ArrowRight, Clock, Zap } from "lucide-react";
import { Layout } from "../components/Layout";
import { ExperienceCard } from "../components/ExperienceCard";
import { CategoryIcon } from "../components/CategoryIcon";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { useI18n } from "../i18n";
import { catName, eur } from "../lib/format";
import * as apiClient from "../api";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1779815348159-19428940b69b?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000";

const SectionHeader = ({ eyebrow, title, sub, to, cta }) => (
  <div className="flex items-end justify-between mb-8 gap-4">
    <div>
      {eyebrow && <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">{eyebrow}</div>}
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight text-slate-900">{title}</h2>
      {sub && <p className="text-slate-500 mt-2 max-w-xl">{sub}</p>}
    </div>
    {to && <Link to={to} className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all whitespace-nowrap">{cta} <ArrowRight className="h-4 w-4" /></Link>}
  </div>
);

const SearchWidget = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: apiClient.getCategories });
  const [date, setDate] = useState(null);
  const [people, setPeople] = useState(2);
  const [category, setCategory] = useState("");

  const submit = () => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (people) params.set("people", people);
    if (date) params.set("date", format(date, "yyyy-MM-dd"));
    navigate(`/esperienze?${params.toString()}`);
  };

  return (
    <div className="glass rounded-2xl p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hero-search-widget">
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid="search-date" className="flex items-center gap-2 rounded-xl bg-white/80 px-4 py-3 text-left">
            <CalendarIcon className="h-4 w-4 text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{t("search.date")}</div>
              <div className="text-sm font-medium text-slate-800 truncate">{date ? format(date, "d MMM yyyy", { locale: it }) : "—"}</div>
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
        </PopoverContent>
      </Popover>
      <label className="flex items-center gap-2 rounded-xl bg-white/80 px-4 py-3">
        <Users className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{t("search.people")}</div>
          <input data-testid="search-people" type="number" min="1" value={people} onChange={(e) => setPeople(e.target.value)} className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none" />
        </div>
      </label>
      <label className="flex items-center gap-2 rounded-xl bg-white/80 px-4 py-3">
        <Search className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{t("search.category")}</div>
          <select data-testid="search-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none">
            <option value="">{t("search.all")}</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name_it}</option>)}
          </select>
        </div>
      </label>
      <button data-testid="search-submit" onClick={submit} className="rounded-xl bg-primary text-white font-semibold px-6 py-3 hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
        <Search className="h-4 w-4" /> {t("search.cta")}
      </button>
    </div>
  );
};

export default function Home() {
  const { t, lang } = useI18n();
  const { data: categories = [] } = useQuery({ queryKey: ["categories", lang], queryFn: apiClient.getCategories });
  const { data: featured = [] } = useQuery({ queryKey: ["featured", lang], queryFn: () => apiClient.getExperiences({ featured: true, limit: 6 }) });
  const { data: tomorrow = [] } = useQuery({ queryKey: ["tomorrow", lang], queryFn: apiClient.getTomorrow });
  const { data: lastMinute = [] } = useQuery({ queryKey: ["lastminute"], queryFn: apiClient.getLastMinute });
  const { data: guides = [] } = useQuery({ queryKey: ["content"], queryFn: apiClient.getContent });
  const { data: sponsors = [] } = useQuery({ queryKey: ["sponsors", "HOME"], queryFn: () => apiClient.getSponsors("HOME") });
  const { data: settings } = useQuery({ queryKey: ["publicSettings"], queryFn: apiClient.getPublicSettings });

  const hero = settings?.hero_image || HERO_FALLBACK;

  const sectionRenderers = {
    categories: () => (
      <section key="categories" id="categorie" className="max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-20 scroll-mt-24" data-testid="home-section-categories">
        <SectionHeader eyebrow="Trova la tua" title="Che tipo sei?" sub="Tranquillo, non giudichiamo. Quasi mai." />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/esperienze?category=${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="group relative rounded-2xl bg-white border border-slate-100 shadow-sm hover-lift animate-float-up overflow-hidden"
              style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}
            >
              {c.image ? (
                <div className="relative h-28 overflow-hidden">
                  <img src={c.image} alt={catName(c, lang)} className="h-full w-full object-cover img-zoom" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-2 left-3 right-3">
                    <div className="font-heading font-semibold text-white text-sm leading-tight drop-shadow">{catName(c, lang)}</div>
                    <div className="text-[11px] text-white/80 mt-0.5">{c.count > 0 ? `${c.count} esperienze` : "Esperienze in arrivo"}</div>
                  </div>
                </div>
              ) : (
                <div className="p-5">
                  <div className="h-11 w-11 rounded-xl bg-primary/8 text-primary flex items-center justify-center mb-3 group-hover:bg-primary group-hover:text-white transition-colors">
                    <CategoryIcon name={c.icon} />
                  </div>
                  <div className="font-heading font-semibold text-slate-900 text-sm leading-tight">{catName(c, lang)}</div>
                  <div className="text-xs text-slate-400 mt-1">{c.count > 0 ? `${c.count} esperienze` : "Esperienze in arrivo"}</div>
                </div>
              )}
            </Link>
          ))}
        </div>
      </section>
    ),
    tomorrow: () => tomorrow.length > 0 && (
      <section key="tomorrow" className="bg-gradient-to-b from-slate-50 to-white mt-24 py-20" data-testid="home-section-tomorrow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeader eyebrow="Domani a Vieste" title={t("home.tomorrow")} sub={t("home.tomorrow.sub")} to="/domani" cta={t("common.viewall")} />
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 -mx-4 px-4 no-scrollbar md:grid md:grid-cols-3 lg:grid-cols-4 md:overflow-visible md:mx-0 md:px-0">
            {tomorrow.slice(0, 8).map((e) => (
              <Link key={e.id} to={`/esperienza/${e.slug}`} data-testid={`tomorrow-card-${e.slug}`} className="snap-start shrink-0 w-[78%] sm:w-[45%] md:w-auto bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover-lift">
                <div className="relative h-36 overflow-hidden">
                  <img src={e.image} alt={e.title} className="h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-2 left-3 right-3 text-white font-heading font-semibold text-sm leading-tight line-clamp-2 drop-shadow">{e.title}</div>
                </div>
                <div className="p-3.5 space-y-1.5">
                  {e.slots.slice(0, 3).map((s) => (
                    <div key={s.time} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 text-slate-700 font-medium"><Clock className="h-3.5 w-3.5 text-primary" />{s.time}</span>
                      <span className={`text-xs font-semibold ${s.status === "few" ? "text-accent" : s.status === "full" ? "text-slate-300" : "text-emerald-600"}`}>
                        {s.status === "few" ? `${t("exp.few")} (${s.available})` : s.status === "full" ? t("exp.full") : t("exp.available")}
                      </span>
                    </div>
                  ))}
                  <div className="pt-1.5 text-xs text-slate-400 flex items-center justify-between">
                    <span>{e.operator_name}</span><span className="font-bold text-primary">{eur(e.price)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    ),
    featured: () => featured.length > 0 && (
      <section key="featured" className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="home-section-featured">
        <SectionHeader eyebrow="Selezione Cristalda" title={t("home.featured")} to="/esperienze" cta={t("common.viewall")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((e, i) => <ExperienceCard key={e.id} exp={e} index={i} />)}
        </div>
      </section>
    ),
    lastminute: () => lastMinute.length > 0 && (
      <section key="lastminute" className="bg-[#0A1128] py-20" data-testid="home-section-lastminute">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-8 gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-2 flex items-center gap-2"><Zap className="h-4 w-4" /> Last Minute</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight text-white">{t("home.lastminute")}</h2>
              <p className="text-white/50 mt-2">{t("home.lastminute.sub")}</p>
            </div>
            <Link to="/last-minute" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-secondary hover:gap-2.5 transition-all">{t("common.viewall")} <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="flex overflow-x-auto snap-x gap-4 pb-4 -mx-4 px-4 no-scrollbar md:grid md:grid-cols-3 lg:grid-cols-4 md:overflow-visible md:mx-0 md:px-0">
            {lastMinute.map((e) => (
              <Link key={e.id} to={`/esperienza/${e.slug || ""}`} data-testid={`lastminute-card-${e.id}`} className="snap-start shrink-0 w-[70%] sm:w-[40%] md:w-auto bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-colors">
                <div className="relative h-32 overflow-hidden">
                  <img src={e.image} alt={e.title} className="h-full w-full object-cover" loading="lazy" />
                  <span className="absolute top-2 right-2 rounded-full bg-accent text-white text-[10px] font-bold px-2 py-0.5">{e.time}</span>
                </div>
                <div className="p-3.5">
                  <div className="font-heading font-semibold text-white text-sm line-clamp-1">{e.title}</div>
                  <div className="text-xs text-accent font-semibold mt-1">{t("exp.few")}: {e.seats_left}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-white/40 line-through text-xs">{eur(e.original_price)}</span>
                    <span className="text-white font-bold">{eur(e.discount_price)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    ),
    concierge: () => (
      <section key="concierge" className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="home-section-concierge">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-[#003A69] p-8 sm:p-14">
          <Sparkles className="absolute -right-6 -top-6 h-40 w-40 text-white/10" />
          <div className="relative max-w-xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-3">Non sai cosa fare?</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-white tracking-tight">{t("concierge.title")}</h2>
            <p className="mt-3 text-white/80">{t("concierge.sub")}</p>
            <Link to="/concierge" data-testid="home-concierge-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-primary font-semibold px-7 py-3 hover:bg-white/90 transition-colors">
              {t("concierge.ask")} <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-4 font-accent italic text-secondary/90 text-sm">Giudichiamo i tuoi gusti solo internamente.</p>
          </div>
        </div>
      </section>
    ),
    guides: () => guides.length > 0 && (
      <section key="guides" className="max-w-7xl mx-auto px-4 sm:px-6 pb-8" data-testid="home-section-guides">
        <SectionHeader eyebrow="Ispirazione" title={t("home.guides")} to="/guide" cta={t("common.viewall")} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {guides.slice(0, 4).map((g) => (
            <Link key={g.id} to="/guide" className="group rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover-lift">
              <div className="relative h-40 overflow-hidden">
                <img src={g.image} alt={g.title} className="h-full w-full object-cover img-zoom" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 text-white font-heading font-semibold text-sm leading-tight line-clamp-2">{g.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    ),
    sponsors: () => sponsors.length > 0 && (
      <section key="sponsors" className="max-w-7xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-100 mt-8" data-testid="home-section-sponsors">
        <div className="text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-6">I nostri partner ufficiali <span className="text-secondary">· {t("common.sponsored")}</span></div>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
          {sponsors.map((s) => {
            const inner = s.image_url
              ? <img src={s.image_url} alt={s.company} className="max-h-16 max-w-[180px] object-contain opacity-80 hover:opacity-100 transition-opacity" />
              : <span className="text-slate-500 font-heading font-semibold text-lg opacity-70 hover:opacity-100 transition-opacity">{s.company}</span>;
            return s.link_url
              ? <a key={s.id} href={s.link_url} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-item-${s.id}`}>{inner}</a>
              : <div key={s.id} data-testid={`sponsor-item-${s.id}`}>{inner}</div>;
          })}
        </div>
      </section>
    ),
  };

  const defaultOrder = ["categories", "tomorrow", "featured", "lastminute", "concierge", "guides", "sponsors"];
  const sections = (settings?.sections && settings.sections.length ? settings.sections : defaultOrder.map((k) => ({ key: k, visible: true })))
    .filter((s) => s.visible !== false)
    .map((s) => s.key)
    .filter((k) => sectionRenderers[k]);

  return (
    <Layout noPadding>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end pb-32 sm:pb-28">
        <img src={hero} alt="Vieste Gargano" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="max-w-2xl animate-float-up">
            <div className="text-xs font-bold uppercase tracking-[0.35em] text-white/80 mb-4">Cristalda Experience · Gargano</div>
            <h1 className="font-heading font-semibold text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight drop-shadow-lg">
              {lang === "it" && settings?.hero_title ? settings.hero_title : t("hero.tagline")}
            </h1>
            <p className="mt-5 text-lg text-white/85 max-w-xl drop-shadow">{lang === "it" && settings?.hero_subtitle ? settings.hero_subtitle : t("hero.subtitle")}</p>
            {settings?.promo_banner ? <p className="mt-4 inline-block rounded-full bg-accent/90 px-4 py-1.5 text-sm font-semibold text-white">{settings.promo_banner}</p> : null}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/esperienze" data-testid="hero-cta-experiences" className="rounded-full bg-secondary text-primary font-semibold px-7 py-3.5 hover:bg-secondary/90 transition-colors flex items-center gap-2">
                Scopri le esperienze <ArrowRight className="h-4 w-4" />
              </Link>
              <button onClick={() => document.getElementById("categorie")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-categories" className="rounded-full bg-white/10 backdrop-blur text-white font-semibold px-7 py-3.5 border border-white/30 hover:bg-white/20 transition-colors">
                Esplora le categorie
              </button>
            </div>
            <p className="mt-6 font-accent italic text-secondary text-xl sm:text-2xl max-w-md">{t("hero.question")}</p>
          </div>
        </div>
        <div className="absolute -bottom-1 inset-x-0 z-20 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto"><SearchWidget /></div>
        </div>
      </section>

      {sections.map((k) => sectionRenderers[k]())}

      {/* DESTINATIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="home-section-destinations">
        <SectionHeader eyebrow="Dove vuoi vivertela?" title="Due mondi. Una sola voglia di partire." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Link to="/esperienze" data-testid="destination-gargano" className="group relative rounded-3xl overflow-hidden h-72 sm:h-80 hover-lift">
            <img src="https://images.unsplash.com/photo-1781940567619-e7591bad408a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Gargano" className="absolute inset-0 h-full w-full object-cover img-zoom" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-6 text-white">
              <span className="self-start rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wide px-3 py-1 mb-3">Disponibile · Prima destinazione</span>
              <h3 className="font-heading font-semibold text-2xl sm:text-3xl">Gargano</h3>
              <p className="font-accent italic text-secondary mt-1">Qui anche perdersi è un'attività.</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2.5 transition-all">Scopri il Gargano <ArrowRight className="h-4 w-4" /></span>
            </div>
          </Link>
          <div data-testid="destination-dolomiti" className="relative rounded-3xl overflow-hidden h-72 sm:h-80">
            <img src="https://images.unsplash.com/photo-1570090090981-1802e36f60cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Dolomiti" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20" />
            <div className="relative h-full flex flex-col justify-end p-6 text-white">
              <span className="self-start rounded-full bg-white/20 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wide px-3 py-1 mb-3 border border-white/30">Coming soon</span>
              <h3 className="font-heading font-semibold text-2xl sm:text-3xl">Dolomiti</h3>
              <p className="font-accent italic text-secondary mt-1">La pianura aveva iniziato ad annoiarci.</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white/70">In arrivo</span>
            </div>
          </div>
        </div>
      </section>

      {/* SHOP TEASER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" data-testid="home-section-shop">
        <div className="relative overflow-hidden rounded-3xl min-h-[300px] flex items-center">
          <img src="https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Prodotti del territorio" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128]/90 via-[#0A1128]/60 to-transparent" />
          <div className="relative p-8 sm:p-14 text-white max-w-xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-3">Dal territorio</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight">Portatelo a casa.</h2>
            <p className="mt-3 text-white/80">Prodotti tipici, artigianato e souvenir scelti nel Gargano e nelle Dolomiti.</p>
            <Link to="/shop" data-testid="home-shop-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-secondary text-primary font-semibold px-7 py-3 hover:bg-secondary/90 transition-colors">
              Scopri lo shop <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* OPERATOR B2B CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" data-testid="home-section-operators">
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-8 sm:p-14">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-3">Sei un operatore?</div>
            <h2 className="text-2xl sm:text-4xl font-heading font-semibold tracking-tight text-slate-900">Tu crea l'esperienza. Al resto pensiamo insieme.</h2>
            <p className="mt-3 text-slate-500 max-w-xl">Porta la tua attività su Cristalda Experience e raggiungi nuovi clienti.</p>
            <Link to="/partner" data-testid="home-operator-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-7 py-3 hover:bg-primary/90 transition-colors">
              Diventa partner <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
